export const CLAIM_TYPE_LABELS = {
  url: "Redirection Url",
  form: "Form",
  quiz: "Quiz",
  offlineClaim: "Offline Claim",
  doctorVisit: "Doctor Visit",
  healthScan: "AI Healthscan",
};

export const QUESTION_TYPE_LABELS = {
  radio: "Multiple choice",
  checkBox: "Checkboxes",
  dropDownSingle: "Dropdown with Single Select",
  dropDownMulti: "Dropdown with Multiple Select",
  textField: "Text Box",
};

export const getClaimTypeLabel = (claimType) =>
  CLAIM_TYPE_LABELS[claimType] || claimType || "-";

export const getQuestionTypeLabel = (questionType) =>
  QUESTION_TYPE_LABELS[questionType] || questionType || "-";

export const formatClaimStatus = (status) => {
  if (!status) {
    return "-";
  }

  const normalized = String(status).toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const formatClaimLanguage = (language) => {
  if (!language) {
    return "-";
  }

  const normalized = String(language).toLowerCase();
  if (normalized === "en") {
    return "English";
  }

  if (normalized === "es") {
    return "Spanish";
  }

  return language;
};

export const normalizeClaimDetail = (response) => {
  const payload = response?.data?.data ?? response?.data ?? response ?? {};
  return payload?.claim ?? payload;
};

export const isCorrectAnswerChoice = (choice, correctAnswer) => {
  if (!correctAnswer || !choice) {
    return false;
  }

  if (Array.isArray(correctAnswer)) {
    return correctAnswer.includes(choice);
  }

  return correctAnswer === choice;
};

const getQuizDataList = (claimDetail = {}) => {
  if (Array.isArray(claimDetail?.quizData)) {
    return claimDetail.quizData;
  }

  if (Array.isArray(claimDetail)) {
    return claimDetail;
  }

  if (claimDetail?.quizData) {
    return [claimDetail.quizData];
  }

  return [];
};

const getQuizDataItem = (claimDetail = {}) => getQuizDataList(claimDetail)[0] ?? {};

export const getQuizMeta = (claimDetail = {}) => {
  const quizDataItem = getQuizDataItem(claimDetail);

  return {
    quizName: claimDetail?.quizName ?? quizDataItem?.quizName ?? "",
    quizDescription:
      claimDetail?.quizDescription ?? quizDataItem?.quizDescription ?? "",
  };
};

export const getQuizSets = (claimDetail = {}) => {
  const quizDataItem = getQuizDataItem(claimDetail);

  return (
    quizDataItem?.quizSets ??
    claimDetail?.quizSets ??
    quizDataItem?.quizSet ??
    claimDetail?.quizSet ??
    []
  );
};

export const getQuizPagination = (claimDetail = {}) => {
  const quizDataItem = getQuizDataItem(claimDetail);

  return {
    currentPage: quizDataItem?.currentPage ?? 1,
    pageSize: quizDataItem?.pageSize ?? 5,
    totalSets: quizDataItem?.totalSets ?? getQuizSets(claimDetail).length,
    totalPages: quizDataItem?.totalPages ?? 1,
  };
};

export const getQuizSetTotalCount = (claimDetail = {}) =>
  getQuizPagination(claimDetail).totalSets;

export const getQuizSetsTableData = (claimDetail = {}) => {
  const { currentPage, pageSize } = getQuizPagination(claimDetail);
  const pageOffset = (currentPage - 1) * pageSize;

  return getQuizSets(claimDetail).map((set, index) => {
    const setNumber = pageOffset + index + 1;

    return {
      key: set?.id ?? `${setNumber}`,
      id: set?.id,
      setNumber,
      setNumberLabel: `Set ${setNumber}`,
      setTitle: set?.setName || "-",
      description: set?.setDescription || "-",
      setName: set?.setName || "",
      setDescription: set?.setDescription || "",
      setQuestions: set?.setQuestions ?? [],
    };
  });
};

export const getSelectedQuizSetDetail = (claimDetail = {}, selectedSet = {}) => {
  const setFromResponse =
    getQuizSets(claimDetail).find((set) => set?.id === selectedSet?.id) ??
    getQuizSets(claimDetail)[0] ??
    selectedSet;

  return {
    setNumber: selectedSet?.setNumber,
    setNumberLabel: selectedSet?.setNumber
      ? `Set ${selectedSet.setNumber}`
      : "Set",
    setName: setFromResponse?.setName || selectedSet?.setName || "-",
    setDescription:
      setFromResponse?.setDescription || selectedSet?.setDescription || "-",
  };
};

export const getQuizSetQuestions = (claimDetail = {}, selectedSet = {}) => {
  const setFromResponse =
    getQuizSets(claimDetail).find((set) => set?.id === selectedSet?.id) ??
    getQuizSets(claimDetail)[0];

  return (
    claimDetail?.formData ??
    setFromResponse?.setQuestions ??
    selectedSet?.setQuestions ??
    []
  );
};

export const getQuizSetQuestionsTotalCount = (claimDetail = {}) =>
  claimDetail?.formTotalCount ??
  claimDetail?.totalFormQuestions ??
  getQuizSetQuestions(claimDetail).length;
